/**
 * Regex that matches concatenated GFM checkbox patterns.
 *
 * Matches `[x]`, `[X]`, or `[ ]` optionally preceded by `- `,
 * followed by text up to the next checkbox or end of string.
 *
 * Examples of matched patterns:
 * - `[x] Item1[x] Item2`
 * - `[ ] Unchecked[x] Checked`
 * - `- [x] Already prefixed- [ ] Mixed`
 */
const CHECKBOX_PATTERN = /(?:-\s*)?\[(x|X|\s)\]\s*/g;

/**
 * Preprocesses raw text from API data to convert concatenated checkbox patterns
 * into proper GitHub Flavored Markdown (GFM) task list format.
 *
 * API data often returns checkbox values as concatenated strings without line breaks,
 * e.g. `"[x] Item1[x] Item2[ ] Item3"`. This function splits them into proper
 * GFM task list items with `- [x]` / `- [ ]` prefix and newline separation.
 *
 * If the input does not contain any checkbox patterns, it is returned unchanged.
 *
 * @param text - Raw text that may contain concatenated checkbox patterns
 * @returns Formatted GFM task list string, or the original text if no checkboxes found
 *
 * @example
 * preprocessMarkdown("[x] Apples[x] Bananas[ ] Oranges")
 * // Returns:
 * // "- [x] Apples\n- [x] Bananas\n- [ ] Oranges"
 *
 * @example
 * preprocessMarkdown("Plain text without checkboxes")
 * // Returns: "Plain text without checkboxes"
 */
export function preprocessMarkdown(text: string): string {
  if (!text || typeof text !== 'string') {
    return text;
  }

  // Check if text contains any checkbox patterns
  const hasCheckboxes = /\[(x|X|\s)\]/.test(text);
  if (!hasCheckboxes) {
    return text;
  }

  // Split the text by checkbox patterns and rebuild as GFM task list
  const items: string[] = [];

  // Reset regex state
  CHECKBOX_PATTERN.lastIndex = 0;

  let match = CHECKBOX_PATTERN.exec(text);
  while (match !== null) {
    // Capture any text before this match that isn't part of a checkbox item
    // (only relevant for the first match if there's leading text)
    if (items.length === 0 && match.index > 0) {
      const leadingText = text.slice(0, match.index).trim();
      if (leadingText) {
        items.push(leadingText);
      }
    }

    const isChecked = match[1] === 'x' || match[1] === 'X';
    const checkboxPrefix = isChecked ? '- [x] ' : '- [ ] ';

    // Find the text after this checkbox until the next checkbox or end of string
    const afterCheckbox = match.index + match[0].length;
    // Look ahead for the next checkbox pattern
    const remaining = text.slice(afterCheckbox);
    const nextCheckboxMatch = /(?:-\s*)?\[(x|X|\s)\]/.exec(remaining);

    const itemText = nextCheckboxMatch
      ? remaining.slice(0, nextCheckboxMatch.index).trim()
      : remaining.trim();

    if (itemText) {
      items.push(`${checkboxPrefix}${itemText}`);
    }

    match = CHECKBOX_PATTERN.exec(text);
  }

  // If we found checkbox items, join them with newlines
  if (items.length > 0) {
    return items.join('\n');
  }

  return text;
}
